import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { localizedText } from '../../i18n/contentLocales';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { deleteChallenge, getChallenge, updateChallenge } from '../../api/content';
import { ApiError } from '../../api/client';
import { useAuth } from '../../auth/useAuth';
import type { Challenge, MultilingualString, ScoringRules } from '../../api/types';
import ConfirmDialog from '../../components/ConfirmDialog';
import ErrorAlert from '../../components/ErrorAlert';
import LoadingSpinner from '../../components/LoadingSpinner';
import MultilingualField from '../../components/MultilingualField';
import ScoringRulesEditor from '../../components/ScoringRulesEditor';
import BackLink from '../../components/BackLink';

const ChallengeDetail: React.FC = () => {
  const { challengeId } = useParams<{ challengeId: string }>();
  const { user } = useAuth();
  const { t, i18n } = useTranslation('content');
  const navigate = useNavigate();

  const [challenge, setChallenge] = useState<Challenge | null>(null);
  const [title, setTitle] = useState<MultilingualString>({});
  const [instructions, setInstructions] = useState<MultilingualString>({});
  const [scoringRules, setScoringRules] = useState<ScoringRules>({ max_points: 10 });
  const [duration, setDuration] = useState('');
  const [solution, setSolution] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const orgSlug = user?.organization_slug;
  const activeLang = i18n.language.startsWith('de') ? 'de' : 'en';
  const titleOf = (ml: MultilingualString) => localizedText(ml, activeLang);

  const load = useCallback(async () => {
    if (!orgSlug || !challengeId) return;
    setLoading(true);
    setError(null);
    try {
      const c = await getChallenge(orgSlug, challengeId);
      setChallenge(c);
      setTitle(c.title);
      setInstructions(c.instructions);
      setScoringRules(c.scoring_rules);
      setDuration(c.time_estimate_minutes != null ? String(c.time_estimate_minutes) : '');
      setSolution(c.suggested_solution ?? '');
    } catch (err) {
      setError(err instanceof ApiError ? err.detail : String(err));
    } finally {
      setLoading(false);
    }
  }, [orgSlug, challengeId]);

  useEffect(() => {
    void load();
  }, [load]);

  const durationValue = duration ? parseInt(duration, 10) : null;
  const isDirty =
    !!challenge &&
    (JSON.stringify(title) !== JSON.stringify(challenge.title) ||
      JSON.stringify(instructions) !== JSON.stringify(challenge.instructions) ||
      JSON.stringify(scoringRules) !== JSON.stringify(challenge.scoring_rules) ||
      durationValue !== challenge.time_estimate_minutes ||
      (solution.trim() || null) !== challenge.suggested_solution);
  const deTitleMissing = !title.de?.trim();
  const deInstructionsMissing = !instructions.de?.trim();

  const usageStations = (challenge?.usage?.station_titles ?? []).map((s) => titleOf(s)).filter(Boolean);
  const usageRallyes = (challenge?.usage?.rallye_titles ?? []).map((r) => titleOf(r)).filter(Boolean);
  const usageCount = challenge?.usage?.station_ids.length ?? 0;

  const handleSave = async () => {
    if (!orgSlug || !challengeId || !challenge) return;
    setSaving(true);
    setError(null);
    try {
      const updated = await updateChallenge(orgSlug, challengeId, {
        title,
        instructions,
        scoring_rules: scoringRules,
        time_estimate_minutes: durationValue,
        suggested_solution: solution.trim() || null,
      });
      setChallenge(updated);
    } catch (err) {
      setError(err instanceof ApiError ? err.detail : String(err));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!orgSlug || !challengeId) return;
    setDeleting(true);
    try {
      await deleteChallenge(orgSlug, challengeId);
      navigate('/challenges');
    } catch (err) {
      setError(err instanceof ApiError ? err.detail : String(err));
      setDeleting(false);
      setDeleteOpen(false);
    }
  };

  if (loading) return <LoadingSpinner />;
  if (!challenge) {
    return (
      <Box>
        <ErrorAlert message={error ?? 'Challenge not found'} onRetry={load} />
      </Box>
    );
  }

  return (
    <Box>
      <BackLink to="/challenges" label={t('challenge.detail.backToList')} />
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        sx={{ justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' }, mb: 3 }}
      >
        <Stack direction="row" spacing={2} sx={{ alignItems: 'center' }}>
          <Typography variant="h2" component="h1">
            {titleOf(challenge.title) || t('challenge.detail.title')}
          </Typography>
          <Chip label={t(`challenge.types.${challenge.challenge_type}`)} size="small" />
        </Stack>
        <Button
          startIcon={<DeleteIcon />}
          color="error"
          variant="outlined"
          onClick={() => setDeleteOpen(true)}
        >
          {t('challenge.detail.delete')}
        </Button>
      </Stack>
      {error && <ErrorAlert message={error} />}
      <Card>
        <CardContent>
          <Typography variant="h5" component="h2" gutterBottom>
            {t('challenge.detail.editTitle')}
          </Typography>
          {usageCount > 0 && (
            <Alert severity="info" sx={{ mb: 2 }}>
              {t('challenge.detail.propagationNote')}
              {usageStations.length > 0 && ` ${t('challenge.detail.usedInStations', { stations: usageStations.join(', ') })}`}
              {usageRallyes.length > 0 && ` ${t('challenge.detail.usedInRallyes', { rallyes: usageRallyes.join(', ') })}`}
            </Alert>
          )}
          <Stack spacing={3}>
            <MultilingualField
              value={title}
              onChange={setTitle}
              label={t('challenge.create.titleLabel')}
              required
            />
            <MultilingualField
              value={instructions}
              onChange={setInstructions}
              label={t('challenge.create.instructionsLabel')}
              required
              richText
            />
            <TextField
              label={t('challenge.create.durationLabel')}
              value={duration}
              onChange={(e) => setDuration(e.target.value)}
              type="number"
              slotProps={{ htmlInput: { min: 0 } }}
              sx={{ maxWidth: 240 }}
            />
            <ScoringRulesEditor
              value={scoringRules}
              onChange={setScoringRules}
              challengeType={challenge.challenge_type}
            />
            <TextField
              label={t('challenge.create.solutionLabel')}
              value={solution}
              onChange={(e) => setSolution(e.target.value)}
              multiline
              minRows={2}
              helperText={t('challenge.create.solutionHelp')}
            />
            <Divider />
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="contained"
                onClick={handleSave}
                disabled={!isDirty || saving || deTitleMissing || deInstructionsMissing}
              >
                {usageCount > 0
                  ? t('challenge.detail.saveAcrossAll')
                  : t('challenge.detail.save')}
              </Button>
            </Box>
          </Stack>
        </CardContent>
      </Card>
      <ConfirmDialog
        open={deleteOpen}
        title={t('challenge.detail.delete')}
        message={
          usageStations.length > 0
            ? t('challenge.detail.deleteInUseConfirm', {
                count: usageStations.length,
                stations: usageStations.join(', '),
              })
            : t('challenge.detail.deleteConfirm')
        }
        destructive
        loading={deleting}
        confirmLabel={t('actions.delete', { ns: 'common' })}
        onConfirm={handleDelete}
        onCancel={() => setDeleteOpen(false)}
      />
    </Box>
  );
};

export default ChallengeDetail;
